import {
  DISPUTE_NOTE_MAX,
  DISPUTE_STATUS_KEYS,
  REBILL_MODE_KEYS,
  REBILL_NOTE_MAX,
  REBILL_REASON_KEYS,
  VARIANCE_FLAG_KEYS,
  disputeMoves,
  disputeText,
  flagText,
  format,
  moveText,
  parseBillForm,
  rebillModeText,
  rebillReasonText,
  toleranceText,
  usd,
  varianceText,
  type BillErrors,
  type BillForm,
  type DisputeStatusKey,
  type RebillModeKey,
  type RebillReasonKey,
  type VarianceFlagKey,
} from "@flexfreight/core";
import {
  Banner,
  Button,
  DataTable,
  DatePicker,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FilterToolbar,
  InlineError,
  KpiTile,
  notify,
  PageHeader,
  SearchInput,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TextAreaField,
  TextField,
  Toggle,
  Truncate,
  pageRows,
  sortRows,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { carrierName, carrierOptions } from "./Shipments";
import { ZohoFailuresSection } from "./ZohoFailures";
import { BackLink, CustomerLink, day, emailOutcomeText, message, money, sortOf, sortPatch, time, useCan, when } from "./ops";

type Bill = { carrierInvoiceNumber: string; invoiceDate: string | null; billedCents: number; quotedCostCents: number; disputeStatus: DisputeStatusKey; disputeNote: string | null; invoiceDocumentId: string | null; updatedAt: string };
export type ReconRow = {
  shipmentId: string;
  orgId: string;
  orgName: string;
  carrier: string;
  status: string;
  bolNumber: string | null;
  proNumber: string | null;
  pickupDate: string | null;
  quotedCostCents: number;
  priceCents: number;
  bill: Bill | null;
  varianceCents: number;
  varianceBps: number | null;
  flag: VarianceFlagKey;
};
type ReconList = {
  rows: ReconRow[];
  truncated: boolean;
  summary: { quotedCostCents: number; billedCents: number; varianceCents: number; counts: Record<VarianceFlagKey | "openDisputes", number> };
  tolerance: { cents: number; bps: number };
};
type Policy = { mode: RebillModeKey; onOverBill: boolean; onLostDispute: boolean };
type Preview = { targetCents: number; applyCents: number };
export type ReconDetail = {
  row: ReconRow & { origin: string; destination: string };
  money: { chargeCents: number; quotedCostCents: number; billedCents: number | null; quotedMarginCents: number; actualMarginCents: number | null; rebilledCents: number };
  rebill: { kind: "rebill" | "credit" | null; preview: { cost: Preview; markup: Preview }; blockedReason: string | null };
  invoice: { id: string; status: string; amountCents: number } | null;
  customer: { id: string; name: string; policy: Policy | null } | null;
  tolerance: { cents: number; bps: number };
};
type EmailOutcome = "queued" | "not_connected" | "not_sent";

/** Signed amount for an invoice change: "+$23.00" / "-$3.00". */
const signed = (cents: number) => (cents > 0 ? `+${format(usd(cents))}` : format(usd(cents)));
const FILTERS = { flag: "", dispute: "", carrier: "", q: "", sort: "", dir: "asc", page: 0 };
const PAGE_SIZE = 50;

/** Freight > Billing reconciliation: carrier bills against the cost quoted at booking, across customers. */
export function BillingPage() {
  const can = useCan();
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const navigate = useNavigate();
  const flag = (VARIANCE_FLAG_KEYS as readonly string[]).includes(v.flag) ? (v.flag as VarianceFlagKey) : null;
  const dispute = (DISPUTE_STATUS_KEYS as readonly string[]).includes(v.dispute) ? (v.dispute as DisputeStatusKey) : null;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const qs = new URLSearchParams({ ...(flag ? { flag } : {}), ...(dispute ? { dispute } : {}) }).toString();
  const list = useQuery({
    queryKey: ["console", "billing", qs],
    queryFn: () => api<ReconList>(`/v1/admin/reconciliation${qs ? `?${qs}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const needle = v.q.toLowerCase();
  const sort = sortOf(v);
  const all = sortRows(
    (list.data?.rows ?? []).filter((r) => (!v.carrier || r.carrier === v.carrier) && (!needle || [r.bolNumber, r.proNumber, r.orgName, r.bill?.carrierInvoiceNumber].some((x) => x?.toLowerCase().includes(needle)))),
    sort,
    (r, k) => (k === "shipment" ? r.bolNumber : k === "customer" ? r.orgName : k === "pickup" ? time(r.pickupDate) : k === "quoted" ? r.quotedCostCents : k === "billed" ? (r.bill?.billedCents ?? null) : k === "variance" ? (r.bill ? r.varianceCents : null) : null),
  );
  const rows = pageRows(all, v.page, PAGE_SIZE);
  const s = list.data?.summary;
  const href = (r: ReconRow) => `/billing/${r.orgId}/${r.shipmentId}`;
  const items: FilterItem[] = [
    { key: "flag", priority: 1, active: flag !== null, inline: <Select variant="toolbar" label="Bill" value={flag ?? ""} onChange={(x) => f.set({ flag: x })} options={[{ value: "", label: "Any" }, ...VARIANCE_FLAG_KEYS.map((k) => ({ value: k, label: flagText(k) }))]} /> },
    { key: "dispute", priority: 2, active: dispute !== null, inline: <Select variant="toolbar" label="Dispute" value={dispute ?? ""} onChange={(x) => f.set({ dispute: x })} options={[{ value: "", label: "Any" }, ...DISPUTE_STATUS_KEYS.map((k) => ({ value: k, label: disputeText(k) }))]} /> },
    { key: "carrier", priority: 3, active: v.carrier !== "", inline: <Select variant="toolbar" label="Carrier" value={v.carrier} onChange={(carrier) => f.set({ carrier })} options={carrierOptions()} /> },
  ];
  const columns: Column<ReconRow>[] = [
    {
      key: "shipment",
      header: "Shipment",
      primary: true,
      sortable: true,
      width: "10rem",
      render: (r) => (
        <>
          <Link className="ffa-rowlink ffa-cell-strong" to={href(r)}>{r.bolNumber ?? "No BOL"}</Link>
          <span className="ffa-block ffa-muted">{carrierName(r.carrier)}{r.proNumber ? ` · PRO ${r.proNumber}` : ""}</span>
        </>
      ),
    },
    { key: "customer", header: "Customer", sortable: true, truncate: "custom", render: (r) => <Truncate text={r.orgName}><CustomerLink orgId={r.orgId} name={r.orgName} /></Truncate> },
    { key: "pickup", header: "Pickup", sortable: true, width: "8rem", mobile: "hide", render: (r) => day(r.pickupDate) },
    { key: "quoted", header: "Quoted cost", numeric: true, sortable: true, width: "8rem", render: (r) => money(r.quotedCostCents) },
    { key: "billed", header: "Carrier bill", numeric: true, sortable: true, width: "9rem", render: (r) => (r.bill ? <>{money(r.bill.billedCents)}<span className="ffa-block ffa-muted">{r.bill.carrierInvoiceNumber}</span></> : <span className="ffa-muted">Not billed</span>) },
    { key: "variance", header: "Variance", numeric: true, sortable: true, width: "9.5rem", render: (r) => (r.bill ? varianceText(r.varianceCents, r.varianceBps) : "-") },
    {
      key: "flag",
      header: "Status",
      width: "8.5rem",
      render: (r) => (
        <span className="ffx-cellstack">
          <StatusBadge domain="billFlag" value={r.flag} />
          {r.bill && r.bill.disputeStatus !== "none" ? <StatusBadge domain="billDispute" value={r.bill.disputeStatus} /> : null}
        </span>
      ),
    },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Billing reconciliation"
        subtitle={`Carrier bills against the cost quoted at booking, newest 200 shipments across customers. A bill within ${list.data ? toleranceText(list.data.tolerance) : "the tolerance"} is a match.`}
      />
      <div className="ffa-kpis" aria-busy={list.isPending || undefined}>
        <KpiTile label="Billed vs quoted" value={s ? money(s.billedCents) : "–"} sub={s ? `quoted ${money(s.quotedCostCents)}` : undefined} />
        <KpiTile label="Variance" value={s ? varianceText(s.varianceCents, null) : "–"} sub="on billed shipments" tone={s && s.varianceCents > 0 ? "danger" : undefined} />
        <KpiTile label="Over" value={s ? String(s.counts.over) : "–"} sub="billed over tolerance" tone={s && s.counts.over > 0 ? "danger" : undefined} />
        <KpiTile label="Unbilled" value={s ? String(s.counts.unbilled) : "–"} sub="no carrier bill yet" />
        <KpiTile label="Open disputes" value={s ? String(s.counts.openDisputes) : "–"} sub="with carriers" />
      </div>
      <ZohoFailuresSection canRetry={can("billing:write")} />
      <FilterToolbar
        label="Reconciliation filters"
        search={<SearchInput label="Search BOL, PRO, customer or carrier invoice #" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
        meta={list.data ? `${all.length} shipment${all.length === 1 ? "" : "s"}` : undefined}
      />
      <DataTable
        caption="Carrier bill reconciliation"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.shipmentId}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load reconciliation", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty="No shipments match."
        rowTone={(r) => (r.flag === "over" ? "danger" : null)}
        sort={sort}
        onSortChange={(x) => f.set(sortPatch(x))}
        onRowClick={(r) => navigate(href(r))}
        rowHref={href}
        pagination={all.length > PAGE_SIZE ? { page: v.page, pageSize: PAGE_SIZE, total: all.length, onPageChange: (page) => f.set({ page }) } : undefined}
      />
      {list.data?.truncated ? <p className="ffa-muted">Showing the newest 200 shipments.</p> : null}
    </section>
  );
}

/** Freight > Billing reconciliation > one shipment: the bill, the dispute, the rebill and the customer's policy. */
export function BillingShipmentPage() {
  const { orgId = "", shipmentId = "" } = useParams();
  const can = useCan();
  const key = ["console", "billing", "detail", orgId, shipmentId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ReconDetail>(`/v1/admin/orgs/${orgId}/reconciliation/${shipmentId}`) });
  const back = <BackLink to="/billing">Billing reconciliation</BackLink>;
  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout header={<PageHeader back={back} eyebrow="Carrier bill" title={<Skeleton width="12rem" height="2.25rem" />} subtitle={<Skeleton width="22rem" />} />} aside={<Section title="Rebill the customer"><SkeletonText lines={5} /></Section>}>
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading shipment…</span>
      </section>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Shipment not found" : "Carrier bill"} />
        {missing ? <Banner tone="warning" title="This shipment doesn't exist.">It may have been removed, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this shipment" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const d = q.data;
  const r = d.row;
  const m = d.money;
  const write = can("billing:write");
  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="Carrier bill"
            title={r.bolNumber ?? "Shipment"}
            badges={<><StatusBadge domain="billFlag" value={r.flag} /><StatusBadge domain="shipment" value={r.status} /></>}
            subtitle={<><CustomerLink orgId={r.orgId} name={r.orgName} /> · {carrierName(r.carrier)} · {r.origin} → {r.destination} · picked up {day(r.pickupDate)}</>}
            primaryAction={<Link className="ffx-btn" to={`/shipments/${r.shipmentId}`}>Open shipment</Link>}
          />
        }
        summary={
          <div className="ffa-kpis">
            <KpiTile label="Customer charge" value={money(m.chargeCents)} sub={m.rebilledCents ? `${signed(m.rebilledCents)} rebilled` : "at booking"} />
            <KpiTile label="Quoted cost" value={money(m.quotedCostCents)} sub={`margin ${money(m.quotedMarginCents)}`} />
            <KpiTile label="Carrier bill" value={money(m.billedCents)} sub={r.bill ? varianceText(r.varianceCents, r.varianceBps) : "not recorded"} tone={r.flag === "over" ? "danger" : undefined} />
            <KpiTile label="Actual margin" value={money(m.actualMarginCents)} sub={m.actualMarginCents === null ? "after the bill" : "charge + rebills - bill"} tone={m.actualMarginCents !== null && m.actualMarginCents < 0 ? "danger" : undefined} />
            <KpiTile label="Customer invoice" value={d.invoice ? money(d.invoice.amountCents) : "-"} sub={d.invoice ? <StatusBadge domain="invoice" value={d.invoice.status} size="sm" /> : "issued on delivery"} />
          </div>
        }
        aside={
          <>
            <RebillCard d={d} write={write} queryKey={key} />
            {d.customer ? <PolicyCard d={d} write={write} queryKey={key} /> : null}
          </>
        }
        asideLabel="Rebill"
      >
        <BillCard d={d} write={write} queryKey={key} />
        {r.bill ? <DisputeCard d={d} write={write} queryKey={key} /> : null}
      </DetailLayout>
    </section>
  );
}

function useRefresh(queryKey: string[]) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey });
    void qc.invalidateQueries({ queryKey: ["console", "billing"] });
  };
}

type WriteOut = ReconRow & { autoRebill: { trigger: string; appliedCents: number; invoiceId: string | null; skipped: string | null } | null };
const autoText = (a: WriteOut["autoRebill"]) => (!a ? undefined : a.skipped ? `Automatic rebill skipped: ${a.skipped}` : a.appliedCents ? `The customer's rebill policy applied ${signed(a.appliedCents)} to their invoice.` : undefined);

function BillCard({ d, write, queryKey }: { d: ReconDetail; write: boolean; queryKey: string[] }) {
  const refresh = useRefresh(queryKey);
  const b = d.row.bill;
  const [form, setForm] = useState<BillForm>(b ? { invoiceNumber: b.carrierInvoiceNumber, invoiceDate: b.invoiceDate ?? "", amount: format(usd(b.billedCents)).replace(/[$,]/g, "") } : { invoiceNumber: "", invoiceDate: "", amount: "" });
  const [errors, setErrors] = useState<BillErrors>({});
  const save = useMutation({
    mutationFn: (body: object) => api<WriteOut>(`/v1/admin/orgs/${d.row.orgId}/reconciliation/${d.row.shipmentId}`, { method: "PUT", body }),
    onSuccess: (r) => {
      refresh();
      notify.success(`Bill recorded: ${flagText(r.flag)}`, autoText(r.autoRebill) ?? (r.flag === "over" ? `${varianceText(r.varianceCents, r.varianceBps)} over the quoted cost.` : undefined));
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const submit = () => {
    const p = parseBillForm(form);
    if ("errors" in p) return setErrors(p.errors);
    setErrors({});
    save.mutate(p.input);
  };
  return (
    <Section title="Carrier bill" description={b ? undefined : `No bill from ${carrierName(d.row.carrier)} recorded yet.`}>
      {b ? (
        <DefinitionList
          items={[
            { term: "Invoice #", detail: b.carrierInvoiceNumber },
            { term: "Invoice date", detail: day(b.invoiceDate) },
            { term: "Billed", detail: <span className="ffx-num">{money(b.billedCents)}</span> },
            { term: "Quoted cost", detail: <span className="ffx-num">{money(b.quotedCostCents)}</span> },
            { term: "Recorded", detail: when(b.updatedAt) },
          ]}
        />
      ) : null}
      {write ? (
        <form
          className="ffa-form"
          aria-label={b ? "Replace the bill" : "Record the bill"}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="ffx-fields">
            <TextField label="Carrier invoice #" value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} error={errors.invoiceNumber} maxLength={64} />
            <DatePicker variant="field" rule="any" cutoffHour={null} label="Invoice date" value={form.invoiceDate || null} onChange={(iso) => setForm({ ...form, invoiceDate: iso })} error={errors.invoiceDate} hint={null} optional />
            <TextField label="Billed amount" prefix="$" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} error={errors.amount} hint={`Quoted cost ${money(d.money.quotedCostCents)}.`} />
          </div>
          <div className="ffx-actions">
            <Button type="submit" variant="primary" loading={save.isPending}>{b ? "Replace bill" : "Record bill"}</Button>
          </div>
        </form>
      ) : null}
    </Section>
  );
}

function DisputeCard({ d, write, queryKey }: { d: ReconDetail; write: boolean; queryKey: string[] }) {
  const refresh = useRefresh(queryKey);
  const { confirm, dialog } = useConfirm();
  const cur = d.row.bill!.disputeStatus;
  const [note, setNote] = useState("");
  const move = useMutation({
    mutationFn: (to: DisputeStatusKey) => api<WriteOut>(`/v1/admin/orgs/${d.row.orgId}/reconciliation/${d.row.shipmentId}/dispute`, { method: "PATCH", body: note.trim() ? { status: to, note: note.trim() } : { status: to } }),
    onSuccess: (r) => {
      refresh();
      setNote("");
      notify.success(disputeText(r.bill!.disputeStatus), autoText(r.autoRebill));
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const go = async (to: DisputeStatusKey) => {
    if ((to === "won" || to === "lost") && !(await confirm({ title: to === "won" ? "Carrier credited the overage?" : "Carrier kept the charge?", body: to === "won" ? "Any earlier rebill on this shipment can then be reversed." : "The overage can then be rebilled to the customer.", confirmLabel: moveText(cur, to) }))) return;
    move.mutate(to);
  };
  return (
    <Section title="Dispute with the carrier" actions={<StatusBadge domain="billDispute" value={cur} />}>
      {d.row.bill!.disputeNote ? <p className="ffa-msg__body">{d.row.bill!.disputeNote}</p> : null}
      {write ? (
        <>
          <TextAreaField label="Note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={DISPUTE_NOTE_MAX} optional />
          <div className="ffx-actions">
            {disputeMoves(cur).map((to) => (
              <Button key={to} variant={to === "open" ? "primary" : "secondary"} loading={move.isPending && move.variables === to} onClick={() => void go(to)}>{moveText(cur, to)}</Button>
            ))}
          </div>
        </>
      ) : null}
      {dialog}
    </Section>
  );
}

function RebillCard({ d, write, queryKey }: { d: ReconDetail; write: boolean; queryKey: string[] }) {
  const refresh = useRefresh(queryKey);
  const { confirm, dialog } = useConfirm();
  const [mode, setMode] = useState<RebillModeKey>(d.customer?.policy?.mode ?? "cost");
  const [reason, setReason] = useState<RebillReasonKey>("reweigh");
  const [note, setNote] = useState("");
  const p = d.rebill.preview[mode];
  const apply = useMutation({
    mutationFn: () => api<{ appliedCents: number; invoiceAmountCents: number; customerEmail: EmailOutcome }>(`/v1/admin/orgs/${d.row.orgId}/reconciliation/${d.row.shipmentId}/rebill`, { method: "POST", body: { reason, mode, ...(note.trim() ? { note: note.trim() } : {}) } }),
    onSuccess: (r) => {
      refresh();
      setNote("");
      notify.success(r.appliedCents === 0 ? "Already applied" : `${signed(r.appliedCents)} on the customer invoice`, [`Amount due is now ${money(r.invoiceAmountCents)}.`, emailOutcomeText(r.customerEmail)].filter(Boolean).join(" "));
    },
    onError: (e) => notify.error("Not applied", message(e)),
  });
  const go = async () => {
    const body = p.applyCents > 0 ? `${signed(p.applyCents)} is added to ${d.row.orgName}'s invoice (${rebillModeText(mode).toLowerCase()}).` : `${money(-p.applyCents)} is credited back on ${d.row.orgName}'s invoice.`;
    if (await confirm({ title: p.applyCents > 0 ? "Rebill the customer?" : "Reverse the rebill?", body, confirmLabel: p.applyCents > 0 ? `Add ${money(p.applyCents)}` : `Credit ${money(-p.applyCents)}` })) apply.mutate();
  };
  const what = d.rebill.kind === "rebill" ? "The carrier billed over tolerance and the charge stands." : d.rebill.kind === "credit" ? "The dispute was won: the carrier credited the overage." : "Nothing to pass on: the bill is within tolerance or not recorded.";
  return (
    <Section title="Rebill the customer" description={what}>
      <DefinitionList
        items={[
          { term: "Rebilled so far", detail: <span className="ffx-num">{money(d.money.rebilledCents)}</span> },
          { term: "At cost", detail: <span className="ffx-num">{money(d.rebill.preview.cost.targetCents)}</span> },
          { term: "With markup", detail: <span className="ffx-num">{money(d.rebill.preview.markup.targetCents)}</span> },
        ]}
      />
      {write ? (
        <>
          <Select label="How" value={mode} onChange={(x) => setMode(x as RebillModeKey)} options={REBILL_MODE_KEYS.map((k) => ({ value: k, label: rebillModeText(k) }))} />
          {d.rebill.kind === "rebill" ? <Select label="Reason" value={reason} onChange={(x) => setReason(x as RebillReasonKey)} options={REBILL_REASON_KEYS.map((k) => ({ value: k, label: rebillReasonText(k) }))} /> : null}
          <TextAreaField label="Note on the invoice" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={REBILL_NOTE_MAX} optional />
          {d.rebill.blockedReason ? <InlineError>{d.rebill.blockedReason}</InlineError> : p.applyCents === 0 ? <p className="ffa-small ffa-muted">The invoice already reflects this. Nothing to apply.</p> : <p className="ffa-small">Applying changes the invoice by <strong className="ffx-num">{signed(p.applyCents)}</strong>.</p>}
          <div className="ffx-actions">
            <Button variant="primary" disabled={!!d.rebill.blockedReason || p.applyCents === 0} loading={apply.isPending} onClick={() => void go()}>{p.applyCents < 0 ? "Reverse rebill" : "Apply rebill"}</Button>
          </div>
        </>
      ) : null}
      {dialog}
    </Section>
  );
}

function PolicyCard({ d, write, queryKey }: { d: ReconDetail; write: boolean; queryKey: string[] }) {
  const refresh = useRefresh(queryKey);
  const c = d.customer!;
  const [mode, setMode] = useState<"off" | RebillModeKey>(c.policy?.mode ?? "off");
  const [overBill, setOverBill] = useState(c.policy?.onOverBill ?? true);
  const [lost, setLost] = useState(c.policy?.onLostDispute ?? true);
  const save = useMutation({
    mutationFn: () => api(`/v1/admin/orgs/${d.row.orgId}/customers/${c.id}/rebill-policy`, { method: "PUT", body: { policy: mode === "off" ? null : { mode, onOverBill: overBill, onLostDispute: lost } } }),
    onSuccess: () => {
      refresh();
      notify.success(mode === "off" ? "Automatic rebills off" : "Rebill policy saved");
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  return (
    <Section title={`Automatic rebills for ${c.name}`}>
      {write ? (
        <>
          <Select label="Policy" value={mode} onChange={(x) => setMode(x as "off" | RebillModeKey)} options={[{ value: "off", label: "Off (rebill by hand)" }, ...REBILL_MODE_KEYS.map((k) => ({ value: k, label: rebillModeText(k) }))]} />
          {mode !== "off" ? (
            <>
              <Toggle look="switch" label="When a bill over tolerance is recorded" checked={overBill} onChange={setOverBill} />
              <Toggle look="switch" label="When a dispute is lost" checked={lost} onChange={setLost} />
            </>
          ) : null}
          <div className="ffx-actions">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save policy</Button>
          </div>
        </>
      ) : (
        <p className="ffa-small">{c.policy ? `${rebillModeText(c.policy.mode)}${c.policy.onOverBill ? ", on over-bills" : ""}${c.policy.onLostDispute ? ", on lost disputes" : ""}.` : "Off: carrier corrections are rebilled by hand."}</p>
      )}
    </Section>
  );
}
